import React, { useState, useRef, useEffect, useCallback } from "react";
import "./VoiceRolePlay.css";
import { useNovaSocket } from "./useNovaSocket";
import { useAudioCapture } from "./useAudioCapture";
import { VoiceRolePlayConfig, TranscriptEntry } from "./types";

interface VoiceRolePlayProps {
  config: VoiceRolePlayConfig;
  onEnd?: (transcript: TranscriptEntry[]) => void;
}

export function VoiceRolePlay({ config, onEnd }: VoiceRolePlayProps) {
  const [status, setStatus] = useState<"idle" | "connecting" | "active" | "ending" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<TranscriptEntry[]>([]);

  const {
    isConnected,
    isAiSpeaking,
    transcript,
    connect,
    disconnect,
    sendAudioChunk,
  } = useNovaSocket({
    wsUrl: config.wsUrl,
    sessionId: config.sessionId,
    orgId: config.orgId,
    userId: config.userId,
    persona: config.persona,
    scenarioName: config.scenarioName,
    scenarioDesc: config.scenarioDesc,
    voiceId: config.voiceId || "matthew",
    onTranscriptUpdate: config.onTranscriptUpdate,
    onError: (err) => {
      setErrorMessage(err.message);
      setStatus("error");
      config.onError?.(err);
    },
  });

  // Keep ref in sync so handleEnd always has latest transcript
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  const { isCapturing, startCapture, stopCapture, error: micError } = useAudioCapture({
    onAudioChunk: sendAudioChunk,
  });

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  useEffect(() => {
    if (isConnected && status === "connecting") {
      setStatus("active");
    }
  }, [isConnected, status]);

  const handleStart = useCallback(async () => {
    setStatus("connecting");
    setErrorMessage("");
    try {
      await connect();
      await startCapture();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to connect");
      setStatus("error");
    }
  }, [connect, startCapture]);

  const handleEnd = useCallback(async () => {
    stopCapture();
    disconnect();
    setStatus("ending");
    const currentTranscript = transcriptRef.current;
    await config.onSessionEnd?.(currentTranscript);
    onEnd?.(currentTranscript);
  }, [stopCapture, disconnect, config, onEnd]);

  return (
    <div className="protrain-voice-rp">
      <div className="protrain-voice-rp-status">
        <span className={`protrain-voice-rp-indicator protrain-voice-rp-indicator--${status}`} />
        <span className="protrain-voice-rp-status-text">
          {status === "idle" && "Ready to start"}
          {status === "connecting" && "Connecting to Nova Sonic..."}
          {status === "active" && (isAiSpeaking ? "Prospect is speaking..." : isCapturing ? "Listening..." : "Connected")}
          {status === "ending" && "Ending session & scoring..."}
          {status === "error" && "Connection error"}
        </span>
      </div>

      <div className="protrain-voice-rp-transcript">
        {transcript.length === 0 && status === "idle" && (
          <div className="protrain-voice-rp-empty">
            Click "Start Conversation" to begin a voice role-play with an AI prospect powered by Amazon Nova Sonic.
            Speak naturally — the AI will listen and respond automatically.
          </div>
        )}
        {transcript.length === 0 && status === "active" && (
          <div className="protrain-voice-rp-empty">Listening... start speaking to the prospect.</div>
        )}
        {transcript.map((entry, i) => (
          <div key={i} className={`protrain-voice-rp-message protrain-voice-rp-message--${entry.role}`}>
            <span className="protrain-voice-rp-message-label">{entry.role === "user" ? "You" : "Prospect"}</span>
            <span className="protrain-voice-rp-message-text">{entry.message}</span>
          </div>
        ))}
        {isAiSpeaking && (
          <div className="protrain-voice-rp-message protrain-voice-rp-message--assistant">
            <span className="protrain-voice-rp-message-label">Prospect</span>
            <span className="protrain-voice-rp-message-text protrain-voice-rp-speaking">Speaking...</span>
          </div>
        )}
        <div ref={transcriptEndRef} />
      </div>

      {(errorMessage || micError) && (
        <div className="protrain-voice-rp-error">{errorMessage || micError}</div>
      )}

      <div className="protrain-voice-rp-controls">
        {status === "idle" || status === "error" ? (
          <button className="protrain-voice-rp-start-btn" onClick={handleStart}>
            🎤 Start Conversation
          </button>
        ) : status === "ending" ? (
          <button className="protrain-voice-rp-end-btn" disabled>
            ⏳ Ending & Scoring...
          </button>
        ) : (
          <div className="protrain-voice-rp-active-controls">
            <div className={`protrain-voice-rp-mic-indicator ${isCapturing ? "protrain-voice-rp-mic-indicator--active" : ""}`}>
              {isCapturing ? "🔴" : "⏸️"}
            </div>
            <button className="protrain-voice-rp-end-btn" onClick={handleEnd}>
              ⏹️ End Session
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
